import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sparkles, Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Capture email reutilisable, 3 variants :
 *  - "section" : grosse section sur la landing
 *  - "footer"  : petite version en bas de page
 *  - "sticky"  : bandeau sticky en bas (dismissable)
 */
export default function NewsletterCapture({ variant = 'section', source = 'landing', dismissible = false, onDismiss }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Merci de saisir un email valide');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email, source, consent: true });
      setSuccess(true);
      toast.success('Inscription confirmee ! A bientot.');
      try { localStorage.setItem('ozcbnb_newsletter', '1'); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Une erreur est survenue, reessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'sticky') {
    if (success) return null;
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3" data-testid="newsletter-sticky">
        <form onSubmit={submit} className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2 text-white text-sm flex-1">
            <Sparkles className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span><strong>Restez informe :</strong> changements de loi (Le Meur), promotions, nouveautes. 1-2 emails par mois max.</span>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="email"
              required
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 sm:w-64 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
              data-testid="newsletter-input-sticky"
            />
            <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5" data-testid="newsletter-submit-sticky">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'M\'inscrire'}
            </Button>
            {dismissible && (
              <button type="button" onClick={onDismiss} className="text-white/40 hover:text-white text-sm px-2" aria-label="Fermer" data-testid="newsletter-dismiss-sticky">x</button>
            )}
          </div>
        </form>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6" data-testid="newsletter-footer">
        {success ? (
          <div className="flex items-center gap-3 text-white">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span>Inscription confirmee ! Merci.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-white mb-1">Newsletter OZCBNB</p>
              <p className="text-xs text-white/50">Nouveautes, conseils Airbnb / Booking, changements de loi. 1-2 emails par mois.</p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <input
                type="email"
                required
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 sm:w-56 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
                data-testid="newsletter-input-footer"
              />
              <Button type="submit" disabled={loading} className="bg-white text-black hover:bg-white/90 rounded-full px-5" data-testid="newsletter-submit-footer">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'OK'}
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }

  // section (default)
  return (
    <section className="py-20 px-4 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-emerald-950" data-testid="newsletter-section">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 text-xs font-semibold text-emerald-300 mb-6">
          <Sparkles className="w-4 h-4" />
          NEWSLETTER OZCBNB
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
          Restez en avance sur le marché
        </h2>
        <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8">
          Recevez nos meilleurs conseils, les changements réglementaires (loi Le Meur 2025-2026),
          les nouveautes du logiciel et nos promotions reservees aux abonnés. 1-2 emails par mois, zero spam.
        </p>

        {success ? (
          <div className="inline-flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-6 py-3 text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-semibold">Inscription confirmee ! Bienvenue.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
            <div className="relative flex-1 w-full">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="email"
                required
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-full pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-emerald-400"
                data-testid="newsletter-input-section"
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-3 h-auto font-semibold" data-testid="newsletter-submit-section">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Je m\'inscris'}
            </Button>
          </form>
        )}

        <p className="text-xs text-white/30 mt-6">
          En vous inscrivant, vous acceptez de recevoir nos communications. Vous pouvez vous désinscrire à tout moment. Conforme RGPD.
        </p>
      </div>
    </section>
  );
}
